import { useState } from 'react'
import { X, Loader2, Plus } from 'lucide-react'
import api from '@/lib/api'

interface CreateRepairModalProps {
  onClose: () => void
  onCreated: () => void
}

const BRANDS = ['Acer', 'Apple', 'ASUS', 'Dell', 'HP', 'Huawei', 'Lenovo', 'LG', 'Microsoft', 'MSI', 'Razer', 'Samsung', 'Toshiba', 'Xiaomi', 'Otro']
const REPAIR_TYPES = [
  { value: 'screen', label: 'Pantalla' },
  { value: 'keyboard', label: 'Teclado' },
  { value: 'motherboard', label: 'Placa base' },
  { value: 'battery', label: 'Batería' },
  { value: 'power', label: 'Carga / Jack' },
  { value: 'hinge', label: 'Bisagras' },
  { value: 'casing', label: 'Carcasa' },
  { value: 'general', label: 'Otro' },
  { value: 'console', label: 'Consola' },
]

export default function CreateRepairModal({ onClose, onCreated }: CreateRepairModalProps) {
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    customer_nif: '',
    customer_type: 'PVP',
    customer_language: 'es',
    device_brand: '',
    device_brand_other: '',
    device_model: '',
    device_serial: '',
    repair_type: 'general',
    problem_description: '',
    urgency: 'normal',
    shipping_option: 'pickup',
    internal_notes: '',
    // Address
    street: '',
    city: '',
    postal_code: '',
    province: '',
    country_code: 'ES',
  })

  const update = (field: string, value: string) => setForm({ ...form, [field]: value })

  const handleSubmit = async () => {
    if (!form.customer_name || !form.customer_email) {
      alert('Nombre y email son obligatorios')
      return
    }
    setSaving(true)
    try {
      const pickup_address = form.street ? {
        street: form.street, city: form.city, postal_code: form.postal_code,
        province: form.province, country_code: form.country_code,
      } : undefined

      await api.post('/repairs/admin/create', {
        customer_name: form.customer_name,
        customer_email: form.customer_email,
        customer_phone: form.customer_phone || undefined,
        customer_nif: form.customer_nif || undefined,
        customer_type: form.customer_type,
        customer_language: form.customer_language,
        device_brand: form.device_brand || undefined,
        device_brand_other: form.device_brand_other || undefined,
        device_model: form.device_model || undefined,
        device_serial: form.device_serial || undefined,
        repair_type: form.repair_type,
        problem_description: form.problem_description || undefined,
        urgency: form.urgency,
        shipping_option: form.shipping_option,
        pickup_address,
        internal_notes: form.internal_notes || undefined,
      })
      onCreated()
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Error creando reparación')
    } finally {
      setSaving(false)
    }
  }

  const inputClass = "w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
  const labelClass = "block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 overflow-y-auto py-8">
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl w-full max-w-2xl mx-4">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-700">
          <h2 className="text-lg font-bold text-slate-900 dark:text-white">Nueva reparación</h2>
          <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-6 max-h-[70vh] overflow-y-auto space-y-5">
          {/* Cliente */}
          <div>
            <h3 className="text-sm font-semibold text-slate-500 uppercase mb-3">Cliente</h3>
            <div className="grid grid-cols-2 gap-3">
              <div><label className={labelClass}>Nombre *</label><input value={form.customer_name} onChange={e => update('customer_name', e.target.value)} className={inputClass} /></div>
              <div><label className={labelClass}>Email *</label><input type="email" value={form.customer_email} onChange={e => update('customer_email', e.target.value)} className={inputClass} /></div>
              <div><label className={labelClass}>Teléfono</label><input value={form.customer_phone} onChange={e => update('customer_phone', e.target.value)} className={inputClass} /></div>
              <div><label className={labelClass}>NIF</label><input value={form.customer_nif} onChange={e => update('customer_nif', e.target.value)} className={inputClass} /></div>
              <div>
                <label className={labelClass}>Tipo cliente</label>
                <select value={form.customer_type} onChange={e => update('customer_type', e.target.value)} className={inputClass}>
                  <option value="PVP">PVP (público)</option>
                  <option value="PVD">PVD (distribuidor)</option>
                  <option value="VIP">VIP</option>
                </select>
              </div>
              <div>
                <label className={labelClass}>Idioma</label>
                <select value={form.customer_language} onChange={e => update('customer_language', e.target.value)} className={inputClass}>
                  <option value="es">Español</option>
                  <option value="pt">Portugués</option>
                  <option value="en">Inglés</option>
                </select>
              </div>
            </div>
          </div>

          {/* Dispositivo */}
          <div>
            <h3 className="text-sm font-semibold text-slate-500 uppercase mb-3">Dispositivo</h3>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelClass}>Marca</label>
                <select value={form.device_brand} onChange={e => update('device_brand', e.target.value)} className={inputClass}>
                  <option value="">Seleccionar...</option>
                  {BRANDS.map(b => <option key={b} value={b}>{b}</option>)}
                </select>
              </div>
              {form.device_brand === 'Otro' && (
                <div><label className={labelClass}>Otra marca</label><input value={form.device_brand_other} onChange={e => update('device_brand_other', e.target.value)} className={inputClass} /></div>
              )}
              <div><label className={labelClass}>Modelo</label><input value={form.device_model} onChange={e => update('device_model', e.target.value)} placeholder="Ej: ThinkPad T480" className={inputClass} /></div>
              <div><label className={labelClass}>Número de serie</label><input value={form.device_serial} onChange={e => update('device_serial', e.target.value)} className={inputClass} /></div>
              <div>
                <label className={labelClass}>Tipo reparación</label>
                <select value={form.repair_type} onChange={e => update('repair_type', e.target.value)} className={inputClass}>
                  {REPAIR_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              <div>
                <label className={labelClass}>Urgencia</label>
                <select value={form.urgency} onChange={e => update('urgency', e.target.value)} className={inputClass}>
                  <option value="normal">Normal</option>
                  <option value="urgente">Urgente</option>
                </select>
              </div>
            </div>
            <div className="mt-3">
              <label className={labelClass}>Descripción del problema</label>
              <textarea value={form.problem_description} onChange={e => update('problem_description', e.target.value)} rows={3} className={inputClass} />
            </div>
          </div>

          {/* Modalidad de entrega */}
          <div>
            <h3 className="text-sm font-semibold text-slate-500 uppercase mb-3">Entrega del equipo</h3>
            <div className="flex gap-3 mb-3">
              <button type="button" onClick={() => update('shipping_option', 'in_store')}
                className={`flex-1 px-4 py-3 rounded-lg border-2 text-sm font-medium transition-colors ${form.shipping_option === 'in_store' ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-300' : 'border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-400 hover:border-slate-300'}`}>
                Entrega en tienda
              </button>
              <button type="button" onClick={() => update('shipping_option', 'pickup')}
                className={`flex-1 px-4 py-3 rounded-lg border-2 text-sm font-medium transition-colors ${form.shipping_option === 'pickup' ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-300' : 'border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-400 hover:border-slate-300'}`}>
                Recogida a domicilio
              </button>
            </div>

            {form.shipping_option === 'pickup' && (
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2"><label className={labelClass}>Calle</label><input value={form.street} onChange={e => update('street', e.target.value)} className={inputClass} /></div>
                <div><label className={labelClass}>Código postal</label><input value={form.postal_code} onChange={e => update('postal_code', e.target.value)} className={inputClass} /></div>
                <div><label className={labelClass}>Ciudad</label><input value={form.city} onChange={e => update('city', e.target.value)} className={inputClass} /></div>
                <div><label className={labelClass}>Provincia</label><input value={form.province} onChange={e => update('province', e.target.value)} className={inputClass} /></div>
                <div>
                  <label className={labelClass}>País</label>
                  <select value={form.country_code} onChange={e => update('country_code', e.target.value)} className={inputClass}>
                    <option value="ES">España</option>
                    <option value="PT">Portugal</option>
                  </select>
                </div>
              </div>
            )}
          </div>

          {/* Notas internas */}
          <div>
            <label className={labelClass}>Notas internas</label>
            <textarea value={form.internal_notes} onChange={e => update('internal_notes', e.target.value)} rows={2} placeholder="Notas internas (no visibles para el cliente)" className={inputClass} />
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-200 dark:border-slate-700">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 dark:text-slate-400">Cancelar</button>
          <button onClick={handleSubmit} disabled={saving}
            className="flex items-center gap-2 px-6 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            Crear reparación
          </button>
        </div>
      </div>
    </div>
  )
}
